import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminLogin } from '@/api/admin';
import { ADMIN_DASHBOARD_PATH } from '@/config/admin-routes';
import { setAnalyticsExcluded } from '@/lib/analytics';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    if (!password.trim()) {
      toast.error('请输入密码');
      return;
    }

    setIsLoading(true);
    try {
      const result = await adminLogin({ password: password.trim() });

      sessionStorage.setItem('admin_token', result.token);
      // 管理员自己的浏览器不计入运营数据。
      setAnalyticsExcluded(true);

      toast.success('登录成功');
      navigate(ADMIN_DASHBOARD_PATH);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        toast.error('密码错误，或短时间内失败次数过多');
      } else if (status === 429) {
        // 限流响应：提示稍后再试，不暴露限流细节与路径信息
        toast.error('登录尝试过于频繁，请稍后再试');
      } else if (status === 500) {
        toast.error('服务器内部错误，请联系管理员');
      } else {
        toast.error('登录失败，请检查网络后重试');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-800">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl">管理员登录</CardTitle>
          <CardDescription>
            请输入管理员密码
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Input
              type="password"
              placeholder="请输入密码"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="h-12"
            />
          </div>
          <Button
            onClick={handleLogin}
            disabled={isLoading || !password.trim()}
            className="h-12 w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                登录中...
              </>
            ) : (
              '登录'
            )}
          </Button>

          <div className="pt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              返回主题列表
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
